/**
 * THE PINNED BOARD, as ONE dashboard tile.
 *
 * ══ WHY ONE TILE AND NOT ONE PER PIN ═══════════════════════════════════════
 *
 * `TileRenderer.tsx` switches on `tile.id` and its `default:` arm returns null
 * SILENTLY. A per-pin tile id would be an unbounded id space that no frozen
 * tile-id list could ever cover, so a pinned board that renders NOWHERE could
 * ship green — which is exactly what happened between step 14 and this step,
 * and what `agent-execution-boundary.test.ts` clause 12b caught on purpose.
 *
 * ══ A PIN CANNOT BECOME A SECOND SOURCE OF NUMBERS ═════════════════════════
 *
 * 🔴 `core.admin_pinned_answer` HAS NO NUMERIC RESULT COLUMN. Every value on
 * this tile was computed by `runMetric` moments ago, under the VIEWING admin's
 * own permissions — so a pinned answer is structurally incapable of becoming
 * the stale figure that contradicts the dashboard. That failure mode, not a
 * wrong answer, is what retired other vendors' query assistants.
 *
 * A pin the viewer may not read renders the em-dash card naming the missing
 * key. The backend decided that; this component does not re-check permissions
 * and must not start.
 */
import { useReducer, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { Pin, PinOff } from 'lucide-react';

import { unpinPinnedAnswer } from '@/hooks/useAgentArtifacts';
import type { AnswerBody, PinnedTileData } from '../protocol';
import { UNPIN_IDLE, shouldSendUnpin, unpinConfirmationText, unpinReducer } from '../unpin-state';
import { RedactedCard } from './Cells';
import { RawTable } from './ResultTable';
import { DeepLink } from './DeepLink';

const CONTROL_BUTTON =
  'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] transition-colors hover:bg-muted disabled:opacity-50';

/**
 * THE UNPIN CONTROL. The server's `DELETE /pin` — which is also the ONLY
 * revocation a share link has — shipped with no button anywhere. Two steps,
 * inline: see `unpin-state.ts` for why, and for the transitions.
 */
function UnpinControl({ pin, onRemoved }: { pin: AnswerBody; onRemoved: (pinId: string) => void }) {
  const apiUrl = useApiUrl();
  const [state, dispatch] = useReducer(unpinReducer, UNPIN_IDLE);

  const confirm = async () => {
    const after = unpinReducer(state, { type: 'confirm' });
    // The reducer makes a second click a no-op, so this can never send twice.
    if (!shouldSendUnpin(state, after)) return;
    dispatch({ type: 'confirm' });
    const outcome = await unpinPinnedAnswer(apiUrl, pin.pin_id);
    if (outcome.ok) onRemoved(pin.pin_id);
    else dispatch({ type: 'failed', message: outcome.message });
  };

  if (state.phase === 'idle' || state.phase === 'failed') {
    return (
      <div className="flex flex-col items-end gap-1">
        <button
          type="button"
          onClick={() => dispatch({ type: 'ask' })}
          className={CONTROL_BUTTON}
          aria-label={`Unpin ${pin.title}`}
        >
          <PinOff className="h-3 w-3" aria-hidden />
          Unpin
        </button>
        {state.phase === 'failed' && (
          <p role="alert" className="text-[11px] text-red-600 dark:text-red-500">
            Not removed: {state.error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div role="group" aria-label={`Confirm unpinning ${pin.title}`} className="w-full space-y-1.5">
      <p role="status" className="text-[11px] text-muted-foreground">
        {unpinConfirmationText(pin)}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void confirm()}
          disabled={state.phase === 'removing'}
          className={CONTROL_BUTTON}
        >
          {state.phase === 'removing' ? 'Removing…' : 'Remove'}
        </button>
        <button
          type="button"
          onClick={() => dispatch({ type: 'keep' })}
          disabled={state.phase === 'removing'}
          className={CONTROL_BUTTON}
        >
          Keep
        </button>
      </div>
    </div>
  );
}

function PinnedAnswer({ pin, onRemoved }: { pin: AnswerBody; onRemoved: (pinId: string) => void }) {
  return (
    <li className="rounded-md border p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-medium">{pin.title}</span>
        {pin.as_of && (
          <span
            className={
              pin.freshness_ok === false
                ? 'text-[11px] font-medium text-amber-600 dark:text-amber-500'
                : 'text-[11px] text-muted-foreground'
            }
          >
            {pin.freshness_ok === false ? `STALE — as of ${pin.as_of}` : `as of ${pin.as_of}`}
          </span>
        )}
        <UnpinControl pin={pin} onRemoved={onRemoved} />
      </div>
      {pin.cell !== null ? (
        <div className="mt-2">
          <RedactedCard cell={pin.cell} />
        </div>
      ) : (
        <div className="mt-2 space-y-2">
          <RawTable
            rows={(pin.rows ?? []) as Record<string, unknown>[]}
            columns={pin.columns}
            caption={pin.title}
          />
          {pin.deep_link && <DeepLink to={pin.deep_link.href} label={pin.deep_link.label} />}
        </div>
      )}
    </li>
  );
}

export function PinnedAnswersTile({ data }: { data: PinnedTileData }) {
  // The dashboard owns the tile data and re-reads it on its own schedule; a pin
  // the server has CONFIRMED deleted leaves this tile at once, without waiting.
  const [removed, setRemoved] = useState<ReadonlySet<string>>(new Set());
  const pins = (Array.isArray(data?.pins) ? data.pins : []).filter((p) => !removed.has(p.pin_id));
  const onRemoved = (pinId: string) => setRemoved((prev) => new Set([...prev, pinId]));
  return (
    <div className="rounded-lg border bg-card p-6 md:col-span-2 lg:col-span-3">
      <div className="flex items-center gap-2">
        <Pin className="h-4 w-4 text-muted-foreground" aria-hidden />
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          Pinned answers
        </span>
      </div>
      {pins.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">
          Nothing pinned yet. Pin an answer from the agent to keep it here — it re-runs every
          time this page loads, so it can never go stale.
        </p>
      ) : (
        <ul className="mt-3 grid gap-3 md:grid-cols-2">
          {pins.map((pin) => (
            <PinnedAnswer key={pin.pin_id} pin={pin} onRemoved={onRemoved} />
          ))}
        </ul>
      )}
    </div>
  );
}
